import React, { useMemo } from "react";

// region Imports - Components
import LimitedChipsField from "@atoms/LimitedChipsField";
// endregion Imports - Components
// region Imports - External libraries
import { Checkbox, TextField } from "unform-material-ui";
import { Grid, CircularProgress, Box, Divider, Popper } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import CheckBoxOutlineBlankIcon from "@material-ui/icons/CheckBoxOutlineBlank";
// endregion Imports - External libraries
// region Imports - Languages
import { ManagementMessages, GlobalMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Shared / Utils
import { Regional } from "@shared/interfaces/regional.interface";
import { Plant } from "@shared/entities/reg_plants.entity";
// endregion Imports - Shared / Utils

interface UserAccessControlProps {
  t: (key: string) => string;
  modalType: "details" | "update" | "register";
  formRef: any;
  loadingOptionsRegionals: boolean;
  loadingOptionsPlants: boolean;
  activeOptionsRegionals: Regional[];
  optionsRegionals: Regional[];
  handleChangeRegional: (value: Regional[]) => void;
  openOptionsRegionals: boolean;
  setOpenOptionsRegionals: (open: boolean) => void;
  activeOptionsPlants: Plant[];
  optionsPlants: Plant[];
  handleChangePlants: (value: Plant[]) => void;
  openOptionsPlants: boolean;
  setOpenOptionsPlants: (open: boolean) => void;
  checkAllPlants: boolean;
  handleCheckAllPlantsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  defineValueAccordingModalType: (detail: any, update: any, register: any) => any;
  userDetails: any;
}
const UserAccessControl: React.FC<UserAccessControlProps> = ({
  t,
  modalType,
  formRef,
  loadingOptionsRegionals,
  loadingOptionsPlants,
  activeOptionsRegionals,
  optionsRegionals,
  handleChangeRegional,
  openOptionsRegionals,
  setOpenOptionsRegionals,
  activeOptionsPlants,
  optionsPlants,
  handleChangePlants,
  openOptionsPlants,
  setOpenOptionsPlants,
  checkAllPlants,
  handleCheckAllPlantsChange,
  defineValueAccordingModalType,
  userDetails
}) => {

  // region Components
  const PlantsPopperComponent = useMemo(() => (props: any) => {
    const { children, ...popperProps } = props;

    return (
      <Popper {...popperProps} style={{ border: "1px solid rgba(149,157,165,0.2)" }}>
        {children}
        <Divider />
        <Box
          style={{
            backgroundColor: "white",
            height: "45px",
            textOverflow: "ellipsis",
            overflow: "hidden",
            whiteSpace: "nowrap"
          }}
        >
          <Checkbox
            name="undefined"
            checked={checkAllPlants}
            onChange={handleCheckAllPlantsChange}
            id="check-all-plants"
            onMouseDown={(event) => event.preventDefault()}
          />
          {t(GlobalMessages.selectAll)}
        </Box>
      </Popper>
    );
  }, [checkAllPlants, handleCheckAllPlantsChange, t]);
  // endregion Components
  // region Constants
  const sortedOptionsRegionals = useMemo(() => {
    return [...optionsRegionals].sort((a, b) => (b.name ? -b.name.localeCompare(a.name) : 0));
  }, [optionsRegionals]);
  const sortedOptionsPlants = useMemo(() => {
    return [...optionsPlants].sort((a, b) => (b.name ? -b.name.localeCompare(a.name as string) : 0));
  }, [optionsPlants]);
  // endregion Constants

  return (
    <>
      <Grid item xs={12} md={12} lg={12}>
        <Autocomplete
          multiple
          fullWidth
          renderTags={(value, getTagProps) => (
            <LimitedChipsField
              data={value}
              limit={2}
              getTagProps={getTagProps}
              labelsFormat={(regional: Regional) => regional.name ?? ""}
              keysFormat={(regional: Regional) => regional.id_regional ?? ""}
            />
          )}
          limitTags={2}
          disableCloseOnSelect
          open={openOptionsRegionals}
          onOpen={() => setOpenOptionsRegionals(true)}
          onClose={() => setOpenOptionsRegionals(false)}
          onChange={(_event, value) => {
            handleChangeRegional(value);
          }}
          value={activeOptionsRegionals}
          getOptionSelected={(option, value) => option.id_regional === value.id_regional}
          getOptionLabel={(option) => option.name as string}
          options={sortedOptionsRegionals}
          disabled={modalType === "details" || optionsRegionals.length === 0}
          loading={loadingOptionsRegionals}
          defaultValue={defineValueAccordingModalType(activeOptionsRegionals, activeOptionsRegionals, [])}
          renderOption={(option, { selected }) => (
            <>
              <Checkbox
                name="undefined"
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected}
              />
              {option.name}
            </>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              name="regionals"
              label={t(ManagementMessages.detailsAuthorizedRegionals)}
              margin="dense"
              variant="outlined"
              InputLabelProps={{
                shrink: activeOptionsRegionals.length > 0
                  || formRef.current?.getFieldValue("regionals")?.length > 0
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingOptionsRegionals ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} md={12} lg={12}>
        <Autocomplete
          multiple
          fullWidth
          renderTags={(value, getTagProps) => (
            <LimitedChipsField
              data={value}
              limit={2}
              getTagProps={getTagProps}
              labelsFormat={(plant: Plant) => plant.name ?? ""}
              keysFormat={(plant: Plant) => plant.id_plant ?? ""}
            />
          )}
          limitTags={2}
          disableCloseOnSelect
          open={openOptionsPlants}
          onOpen={() => setOpenOptionsPlants(true)}
          onClose={() => setOpenOptionsPlants(false)}
          onChange={(_event, newValue, reason) => {
            if (reason === "clear") {
              // This part should technically be handled by the parent or hook passed as prop
              // Since handleChangePlants expects Plant[], we can pass []
              handleChangePlants([]);
            } else {
              handleChangePlants(newValue);
            }
          }}
          value={activeOptionsPlants}
          getOptionSelected={(option, value) => option.id_plant === value.id_plant}
          getOptionLabel={(option) => option.name as string}
          options={sortedOptionsPlants}
          disabled={modalType === "details"}
          loading={loadingOptionsPlants}
          defaultValue={defineValueAccordingModalType(userDetails.plants, userDetails.plants, [])}
          PopperComponent={PlantsPopperComponent}
          renderOption={(option, { selected }) => (
            <>
              <Checkbox
                name="undefined"
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected || checkAllPlants}
              />
              {option.name}
            </>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              name="plants"
              label={t(ManagementMessages.detailsAuthorizedPlants)}
              margin="dense"
              variant="outlined"
              helperText={t(GlobalMessages.required)}
              InputLabelProps={{
                shrink: activeOptionsPlants.length > 0
                  || formRef.current?.getFieldValue("plants")?.length > 0
              }}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingOptionsPlants ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
        />
      </Grid>
    </>
  );
};

export default UserAccessControl;
